/**
 * Safe Telemetry - Resilient event tracking that never fails
 *
 * Features:
 * - Three tiers: silent (fire-forget), buffered (async retry), guaranteed (sync retry)
 * - Circuit breaker to prevent cascading failures
 * - LocalStorage backup for critical events
 * - SendBeacon API for page unload events
 *
 * @example
 * ```typescript
 * import { trackEvent, TelemetryTier } from '@/lib/telemetry/safe-telemetry'
 *
 * // Silent - non-critical events
 * trackEvent('page.viewed', { page: '/store' }, 'silent')
 *
 * // Buffered - business events with async retry
 * trackEvent('cart.item.added', { itemId: 123 }, 'buffered')
 *
 * // Guaranteed - revenue-critical events
 * trackEvent('payment.succeeded', { orderId: 456 }, 'guaranteed')
 * ```
 */
import { logger } from '@/lib/logger'

// ============================================================================
// Types
// ============================================================================

export type TelemetryTier = 'silent' | 'buffered' | 'guaranteed'

export interface TelemetryEvent {
  name: string
  attributes: Record<string, unknown>
  tier: TelemetryTier
  timestamp: number
  retryCount?: number
}

interface CircuitBreakerState {
  failureCount: number
  circuitOpenUntil: number
  lastFailure: number
}

// ============================================================================
// Configuration
// ============================================================================

const CONFIG = {
  /** Number of failures before circuit opens */
  FAILURE_THRESHOLD: 5,
  /** How long circuit stays open (5 minutes) */
  COOLDOWN_MS: 5 * 60 * 1000,
  /** Max events in buffer */
  MAX_BUFFER_SIZE: 100,
  /** Max events in localStorage backup */
  MAX_BACKUP_SIZE: 50,
  /** Timeout for telemetry requests */
  REQUEST_TIMEOUT_MS: 5000,
  /** Delay between buffer processing */
  BUFFER_PROCESS_DELAY_MS: 50,
  /** Max retries for guaranteed events */
  GUARANTEED_RETRIES: 3,
  /** LocalStorage key for backup */
  BACKUP_STORAGE_KEY: 'payk12_telemetry_backup',
  /** Telemetry endpoint */
  ENDPOINT: '/api/v2/telemetry/event',
} as const

// ============================================================================
// State
// ============================================================================

const circuitBreaker: CircuitBreakerState = {
  failureCount: 0,
  circuitOpenUntil: 0,
  lastFailure: 0,
}

const eventBuffer: TelemetryEvent[] = []
let bufferProcessing = false
let initialized = false

// Global business entity context merged into every event
let globalContext: Record<string, string | number> = {}

// ============================================================================
// Circuit Breaker
// ============================================================================

function isCircuitOpen(): boolean {
  const now = Date.now()

  // Check if circuit should reset
  if (circuitBreaker.circuitOpenUntil > 0 && now >= circuitBreaker.circuitOpenUntil) {
    circuitBreaker.failureCount = 0
    circuitBreaker.circuitOpenUntil = 0
    logger.debug('[SafeTelemetry] Circuit breaker reset')
  }

  return now < circuitBreaker.circuitOpenUntil
}

function recordFailure(): void {
  circuitBreaker.failureCount++
  circuitBreaker.lastFailure = Date.now()

  if (circuitBreaker.failureCount >= CONFIG.FAILURE_THRESHOLD) {
    circuitBreaker.circuitOpenUntil = Date.now() + CONFIG.COOLDOWN_MS
    logger.warn('[SafeTelemetry] Circuit breaker opened - telemetry disabled for 5 minutes')
  }
}

function recordSuccess(): void {
  circuitBreaker.failureCount = 0
}

// ============================================================================
// Transport
// ============================================================================

async function sendEvent(event: TelemetryEvent): Promise<boolean> {
  try {
    // Check for New Relic Browser agent
    if (typeof window !== 'undefined' && (window as any).newrelic?.addPageAction) {
      ;(window as any).newrelic.addPageAction(event.name, {
        ...event.attributes,
        tier: event.tier,
        timestamp: event.timestamp,
      })
      recordSuccess()
      return true
    }

    // Fallback to API endpoint
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), CONFIG.REQUEST_TIMEOUT_MS)

    try {
      const response = await fetch(CONFIG.ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(event),
        signal: controller.signal,
        keepalive: true,
      })

      clearTimeout(timeoutId)

      if (response.ok) {
        recordSuccess()
        return true
      }

      recordFailure()
      return false
    } catch (fetchError) {
      clearTimeout(timeoutId)
      recordFailure()
      return false
    }
  } catch {
    recordFailure()
    return false
  }
}

function sendEventBeacon(event: TelemetryEvent): boolean {
  try {
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(event)], { type: 'application/json' })
      return navigator.sendBeacon(CONFIG.ENDPOINT, blob)
    }
    return false
  } catch {
    return false
  }
}

// ============================================================================
// Buffer Management
// ============================================================================

async function processBuffer(): Promise<void> {
  if (bufferProcessing || eventBuffer.length === 0) return

  bufferProcessing = true

  try {
    while (eventBuffer.length > 0) {
      if (isCircuitOpen()) {
        // Circuit is open, stop processing
        break
      }

      const event = eventBuffer.shift()
      if (event) {
        await sendEvent(event)
        // Rate limit to prevent flooding
        await new Promise((resolve) => setTimeout(resolve, CONFIG.BUFFER_PROCESS_DELAY_MS))
      }
    }
  } finally {
    bufferProcessing = false
  }
}

function addToBuffer(event: TelemetryEvent): void {
  if (eventBuffer.length >= CONFIG.MAX_BUFFER_SIZE) {
    // Remove oldest event
    eventBuffer.shift()
  }
  eventBuffer.push(event)

  // Trigger async processing
  setTimeout(() => processBuffer(), 0)
}

// ============================================================================
// Backup Storage
// ============================================================================

function saveToBackup(event: TelemetryEvent): void {
  try {
    if (typeof localStorage === 'undefined') return

    const stored = JSON.parse(localStorage.getItem(CONFIG.BACKUP_STORAGE_KEY) || '[]')
    stored.push(event)

    // Keep only the most recent events
    const trimmed = stored.slice(-CONFIG.MAX_BACKUP_SIZE)
    localStorage.setItem(CONFIG.BACKUP_STORAGE_KEY, JSON.stringify(trimmed))
  } catch {
    // Storage full or unavailable - silently fail
  }
}

function loadFromBackup(): TelemetryEvent[] {
  try {
    if (typeof localStorage === 'undefined') return []

    const stored = localStorage.getItem(CONFIG.BACKUP_STORAGE_KEY)
    if (!stored) return []

    const events = JSON.parse(stored) as TelemetryEvent[]
    localStorage.removeItem(CONFIG.BACKUP_STORAGE_KEY)
    return events
  } catch {
    return []
  }
}

// ============================================================================
// Guaranteed Delivery
// ============================================================================

async function sendWithRetry(event: TelemetryEvent, maxRetries: number): Promise<void> {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    if (await sendEvent(event)) {
      return // Success
    }

    // Exponential backoff
    const delay = Math.min(100 * Math.pow(2, attempt), 1000)
    await new Promise((resolve) => setTimeout(resolve, delay))
  }

  // All retries failed - save to backup
  saveToBackup(event)
}

// ============================================================================
// Initialization
// ============================================================================

function initialize(): void {
  if (initialized || typeof window === 'undefined') return

  initialized = true

  // Process backed-up events on page load
  window.addEventListener('load', () => {
    try {
      const backedUpEvents = loadFromBackup()
      if (backedUpEvents.length > 0) {
        logger.debug(`[SafeTelemetry] Processing ${backedUpEvents.length} backed-up events`)
        backedUpEvents.forEach((event) => addToBuffer(event))
      }
    } catch {
      // Silently fail
    }
  })

  // Flush buffer before page unload
  window.addEventListener('beforeunload', () => {
    // Send remaining buffered events via beacon
    eventBuffer.forEach((event) => {
      sendEventBeacon(event)
    })
  })

  logger.debug('[SafeTelemetry] Initialized')
}

// ============================================================================
// Global Context
// ============================================================================

/**
 * Set global business entity context that is merged into every tracked event.
 * Call this after JWT session decode to propagate tenant/school context.
 *
 * @example
 * ```typescript
 * setTelemetryContext({
 *   tenantId: session.tenantId,
 *   schoolId: session.schoolId,
 *   merchantId: session.merchantId,
 *   userId: session.userId,
 *   role: session.role,
 * })
 * ```
 */
/** JWT `sids` is often a comma-joined string, not number[]. */
export function toNumericIdList(value: unknown): number[] {
  if (Array.isArray(value)) {
    return value.map(Number).filter((n) => Number.isFinite(n))
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return [value]
  }
  if (typeof value === 'string' && value.trim()) {
    return value
      .split(',')
      .map((part) => Number(part.trim()))
      .filter((n) => Number.isFinite(n))
  }
  return []
}

export function setTelemetryContext(ctx: {
  tenantId?: number
  schoolId?: number
  schoolIds?: unknown
  merchantId?: string
  userId?: number
  role?: string
}): void {
  try {
    const schoolIds = toNumericIdList(ctx.schoolIds)
    const schoolId = ctx.schoolId ?? schoolIds[0]
    globalContext = {
      ...(ctx.tenantId != null && { 'tenant.id': ctx.tenantId }),
      ...(schoolId != null && { 'school.id': schoolId }),
      ...(schoolIds.length > 0 && { 'school.ids': schoolIds.join(',') }),
      ...(ctx.merchantId != null && { 'merchant.id': ctx.merchantId }),
      ...(ctx.userId != null && { 'user.id': ctx.userId }),
      ...(ctx.role != null && { 'user.role': ctx.role }),
    }
  } catch (error) {
    logger.warn('[SafeTelemetry] Failed to set context', { error })
  }
}

/**
 * Clear the global telemetry context (e.g., on logout).
 */
export function clearTelemetryContext(): void {
  globalContext = {}
}

// ============================================================================
// Public API
// ============================================================================

/**
 * Track a telemetry event with appropriate failure handling based on tier.
 *
 * @param name - Event name (e.g., 'cart.item.added')
 * @param attributes - Event attributes
 * @param tier - Delivery tier: 'silent' | 'buffered' | 'guaranteed'
 *
 * @example
 * ```typescript
 * // Non-critical event - fire and forget
 * trackEvent('page.viewed', { page: '/store' }, 'silent')
 *
 * // Business event - async retry
 * trackEvent('cart.item.added', { itemId: 123, amount: 25.00 }, 'buffered')
 *
 * // Revenue event - guaranteed delivery
 * trackEvent('payment.succeeded', { orderId: 456, amount: 100.00 }, 'guaranteed')
 * ```
 */
export function trackEvent(name: string, attributes: Record<string, unknown>, tier: TelemetryTier = 'silent'): void {
  // Absolute safety wrapper - never let telemetry crash the app
  try {
    // Initialize on first call
    if (!initialized) {
      initialize()
    }

    // Skip if circuit is open (except guaranteed events)
    if (isCircuitOpen() && tier !== 'guaranteed') {
      return
    }

    const event: TelemetryEvent = {
      name,
      attributes: {
        ...globalContext,
        ...attributes,
        url: typeof window !== 'undefined' ? window.location.pathname : undefined,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      },
      tier,
      timestamp: Date.now(),
    }

    switch (tier) {
      case 'silent':
        // Fire and forget - don't even wait for response
        sendEvent(event).catch(() => {})
        break

      case 'buffered':
        // Add to buffer for async processing with retry
        addToBuffer(event)
        break

      case 'guaranteed':
        // Synchronous retry with backup
        sendWithRetry(event, CONFIG.GUARANTEED_RETRIES).catch(() => {
          // Last resort - try beacon API
          sendEventBeacon(event)
        })
        break
    }
  } catch {
    // Absolute last resort - never let telemetry crash the app
    if (process.env.NODE_ENV === 'development') {
      console.warn('[SafeTelemetry] Failed to track event:', name)
    }
  }
}

/**
 * Track an event using the beacon API (useful for page unload)
 */
export function trackEventBeacon(name: string, attributes: Record<string, unknown>): void {
  try {
    const event: TelemetryEvent = {
      name,
      attributes: {
        ...attributes,
        url: typeof window !== 'undefined' ? window.location.pathname : undefined,
      },
      tier: 'guaranteed',
      timestamp: Date.now(),
    }
    sendEventBeacon(event)
  } catch {
    // Silently fail
  }
}

/**
 * Get current circuit breaker status (for debugging)
 */
export function getCircuitBreakerStatus(): {
  isOpen: boolean
  failureCount: number
  opensAt: number | null
} {
  return {
    isOpen: isCircuitOpen(),
    failureCount: circuitBreaker.failureCount,
    opensAt: circuitBreaker.circuitOpenUntil > 0 ? circuitBreaker.circuitOpenUntil : null,
  }
}

/**
 * Get buffer status (for debugging)
 */
export function getBufferStatus(): { size: number; processing: boolean } {
  return {
    size: eventBuffer.length,
    processing: bufferProcessing,
  }
}

// Auto-initialize when imported in browser
if (typeof window !== 'undefined') {
  initialize()
}
